/**
 * Accessible, independent tab controller for product information.
 * Blocks can be re-ordered by Shopify without hard-coded IDs.
 */
class FastbidProductTabs extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onEditorBlockSelect = this.onEditorBlockSelect.bind(this);
  }

  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  connectedCallback() {
    if (this.hasAttribute('data-tabs-ready')) return;
    this.setAttribute('data-tabs-ready', '');
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('shopify:block:select', this.onEditorBlockSelect);
    const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    if (selected) this.activate(selected, false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('shopify:block:select', this.onEditorBlockSelect);
    this.removeAttribute('data-tabs-ready');
  }

  activate(tab, shouldFocus = true) {
    if (!tab || !this.contains(tab)) return;
    for (const button of this.tabs) {
      const active = button === tab;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
      const panel = this.querySelectorAll('[role="tabpanel"]');
      for (const candidate of panel) {
        if (candidate.id === button.getAttribute('aria-controls')) {
          candidate.hidden = !active;
        }
      }
    }

    // Do not move the document viewport when a product page loads.
    // Only user-initiated tab selection should move focus / the tab strip.
    if (shouldFocus) {
      tab.focus({ preventScroll: true });
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[role="tab"]');
    if (button && this.contains(button)) this.activate(button);
  }

  onKeyDown(event) {
    if (!(event.target instanceof Element)) return;
    const current = event.target.closest('[role="tab"]');
    const tabs = this.tabs;
    if (!current || !tabs.includes(current)) return;

    const index = tabs.indexOf(current);
    let next = null;
    switch (event.key) {
      case 'ArrowRight':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowLeft':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(next);
  }

  onEditorBlockSelect(event) {
    const id = event.detail?.blockId;
    if (!id) return;
    const button = this.tabs.find((tab) => tab.dataset.blockId === id);
    if (button && this.contains(event.target)) this.activate(button, false);
  }
}

if (!customElements.get('fb-product-tabs')) {
  customElements.define('fb-product-tabs', FastbidProductTabs);
}
